import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import {
  BenefitDTOBenefitEnum,
  CouponBenefitDetailsDTO,
  CreditCardBenefitDetailsDTO,
  CreditCardBenefitDetailsDTOStatusEnum,
  FitnessBenefitDetailsDTO,
  FitnessBenefitDetailsDTOStatusEnum,
  WellpassBenefitDetailsDTO,
  WellpassBenefitDetailsDTOStatusEnum,
} from 'probonio-shared-ui/api';
import { apis, useEmployeeQuery } from 'probonio-shared-ui/module/api';
import { catchNotFound } from 'probonio-shared-ui/module/error';
import { useActiveEmployment } from 'probonio-shared-ui/module/me';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { FITNESS_REGISTER_DEADLINE } from '../../fitness/const';
import { WELLPASS_REGISTER_DEADLINE, WELLPASS_REGISTERED_AT } from '../../wellpass/consts';
import { NonCashBenefit } from '../consts/consts';
import { useNonCashBenefitUsage } from './useNonCashBenefitUsage';

type NonCashDetailsMap = {
  [NonCashBenefit.CreditCard]: CreditCardBenefitDetailsDTO;
  [NonCashBenefit.Fitness]: FitnessBenefitDetailsDTO;
  [NonCashBenefit.Wellpass]: WellpassBenefitDetailsDTO;
};

type SupportedNonCashBenefit = keyof NonCashDetailsMap;

type NonCashDetails = NonCashDetailsMap[SupportedNonCashBenefit];

type NonCashDetailsFor<T extends SupportedNonCashBenefit> = NonCashDetailsMap[T];

interface NonCashBenefitQuery<T extends NonCashDetails> {
  status: NonCashStatus;
  startDate?: Date;
  endDate?: Date;
  nonCashBenefitUsageId?: string;
  isLoading: boolean;
  monthlyBudget?: number;
  deadlineDate?: Date;
  nextEndDate?: Date;
  nextStartDate?: Date;
  otherUsageBenefit?: NonCashBenefit;
  nonCashBenefits?: NonCashBenefit[];
  details?: T;
}

export const NonCashBenefitStatusConfig = {
  ...CreditCardBenefitDetailsDTOStatusEnum,
  ...FitnessBenefitDetailsDTOStatusEnum,
  ...WellpassBenefitDetailsDTOStatusEnum,
  Registering: 'REGISTERING',
  OtherUsage: 'OTHER_USAGE',
} as const;

export type NonCashStatus = (typeof NonCashBenefitStatusConfig)[keyof typeof NonCashBenefitStatusConfig];

const NonCashConfig: Record<
  string,
  { api: string; getStatus: (status: NonCashStatus | undefined, couponsDetails: CouponBenefitDetailsDTO | undefined) => NonCashStatus }
> = {
  [NonCashBenefit.CreditCard]: {
    api: 'creditCard',
    getStatus: (status: NonCashStatus | undefined) => status ?? CreditCardBenefitDetailsDTOStatusEnum.Inactive,
  },
  [NonCashBenefit.Fitness]: { api: 'fitness', getStatus: (status: NonCashStatus | undefined) => getFitnessStatus(status) },
  [NonCashBenefit.Coupons]: {
    api: 'coupons',
    getStatus: (status: NonCashStatus | undefined) => status || NonCashBenefitStatusConfig.Inactive,
  },
  [NonCashBenefit.Wellpass]: {
    api: 'wellpass',
    getStatus: (status: NonCashStatus | undefined, couponsDetails: CouponBenefitDetailsDTO | undefined) =>
      getWellpassStatus(status, couponsDetails),
  },
};

export const getWellpassStatus = (status: NonCashStatus | undefined, couponsDetails?: CouponBenefitDetailsDTO): NonCashStatus => {
  const storedDeadlinedate = localStorage.getItem(WELLPASS_REGISTER_DEADLINE);
  const storedRegisteredAt = localStorage.getItem(WELLPASS_REGISTERED_AT);

  if (
    status === NonCashBenefitStatusConfig.Inactive &&
    storedDeadlinedate !== null &&
    DateTime.fromISO(storedDeadlinedate).isValid &&
    // compare utc because sent from api as utc
    DateTime.fromISO(storedDeadlinedate) > DateTime.now() &&
    !storedRegisteredAt
  ) {
    return NonCashBenefitStatusConfig.Registering;
  } else if (status === NonCashBenefitStatusConfig.Inactive && storedRegisteredAt !== null) {
    return NonCashBenefitStatusConfig.MembershipPending;
  }

  if (couponsDetails?.activeNonCashBenefitNextMonth && couponsDetails.activeNonCashBenefitNextMonth !== NonCashBenefit.Wellpass) {
    return NonCashBenefitStatusConfig.OtherUsage;
  }

  return status || NonCashBenefitStatusConfig.Inactive;
};

export const getFitnessStatus = (status: NonCashStatus | undefined): NonCashStatus => {
  const storedDeadlinedate = localStorage.getItem(FITNESS_REGISTER_DEADLINE);
  if (
    status === NonCashBenefitStatusConfig.Inactive &&
    storedDeadlinedate !== null &&
    DateTime.fromISO(storedDeadlinedate).isValid &&
    // compare utc because sent from api as utc
    DateTime.fromISO(storedDeadlinedate) > DateTime.now()
  ) {
    return NonCashBenefitStatusConfig.Registering;
  }
  return status || NonCashBenefitStatusConfig.Inactive;
};

function useNonCashBenefitQuery<TNonCashBenefit extends SupportedNonCashBenefit>(
  benefit: TNonCashBenefit,
): NonCashBenefitQuery<NonCashDetailsFor<TNonCashBenefit>> {
  const nonCashQuery = useEmployeeQuery(['benefits', NonCashConfig[benefit].api, apis.benefits.getBenefit.name], params =>
    apis.benefits
      .getBenefit({
        ...params,
        benefit,
      })
      .then(res => res.data)
      // A deactivated non-cash benefit returns 404; treat it as "no details" instead of an error
      // toast, so residual states (e.g. credit card with remaining balance) render cleanly.
      .catch(catchNotFound),
  );
  const data = nonCashQuery.data?.details as NonCashDetailsFor<TNonCashBenefit> | undefined;
  const couponsQuery = useEmployeeQuery(
    ['benefits', 'coupons', apis.benefits.getBenefit.name],
    params =>
      apis.benefits
        .getBenefit({
          ...params,
          benefit: BenefitDTOBenefitEnum.Coupons,
        })
        .then(res => res.data),
    {
      enabled: !!data && data.status !== NonCashBenefitStatusConfig.Expiring,
    },
  );

  const couponsData = couponsQuery.data?.details as CouponBenefitDetailsDTO | undefined;

  const benefitUsage = useNonCashBenefitUsage(data, couponsData, benefit);

  return useMemo(() => {
    return {
      status: benefitUsage?.status ?? NonCashConfig[benefit].getStatus(data?.status, couponsData),
      nonCashBenefits: couponsData?.nonCashBenefits ?? undefined,
      startDate: data?.startDate ? DateTime.fromISO(data?.startDate).toJSDate() : undefined,
      endDate: data?.endDate ? DateTime.fromISO(data?.endDate).toJSDate() : undefined,
      nonCashBenefitUsageId: data?.nonCashBenefitUsageId,
      isLoading: nonCashQuery.isLoading,
      monthlyBudget: data?.monthlyBudget,
      deadlineDate: data?.deadlineDate
        ? DateTime.fromISO(data?.deadlineDate, { zone: 'utc' }).setZone('local', { keepLocalTime: true }).toJSDate()
        : undefined,
      nextStartDate: data?.nextStartDate ? DateTime.fromISO(data.nextStartDate).toJSDate() : undefined,
      // prevent month overflow with timezones
      nextEndDate: data?.nextEndDate ? DateTime.fromISO(data?.nextEndDate).minus({ day: 1 }).endOf('month').toJSDate() : undefined,
      otherUsageBenefit: benefitUsage?.usageBenefit ?? undefined,
      details: data,
    };
  }, [benefitUsage?.status, benefitUsage?.usageBenefit, benefit, data, couponsData, nonCashQuery.isLoading]);
}

export type NonCashBenefitState<T extends NonCashDetails> = NonCashBenefitQuery<T> & {
  invalidateStatus: () => void;
};

const NonCashBenefitContext = createContext<
  NonCashBenefitState<FitnessBenefitDetailsDTO | CreditCardBenefitDetailsDTO | WellpassBenefitDetailsDTO> | undefined
>(undefined);

export const NonCashBenefitStateProvider: React.FC<React.PropsWithChildren & { benefit: SupportedNonCashBenefit }> = ({
  children,
  benefit,
}) => {
  const nonCashState = useNonCashBenefitQuery(benefit);
  const queryClient = useQueryClient();

  const getActiveEmployment = useActiveEmployment();

  const invalidateStatus = useCallback(() => {
    void queryClient.resetQueries({
      queryKey: [
        'tenants',
        getActiveEmployment().tenantId,
        'employees',
        getActiveEmployment().id,
        'benefits',
        NonCashConfig[benefit].api,
        apis.benefits.getBenefit.name,
      ],
    });
  }, [benefit, getActiveEmployment, queryClient]);

  const context = useMemo(
    () => ({
      invalidateStatus,
      ...nonCashState,
    }),
    [nonCashState, invalidateStatus],
  );
  return <NonCashBenefitContext.Provider value={context}>{children}</NonCashBenefitContext.Provider>;
};

export function useNonCashBenefitState<TNonCashBenefit extends SupportedNonCashBenefit>(): NonCashBenefitState<
  NonCashDetailsFor<TNonCashBenefit>
> {
  const ctx = useContext(NonCashBenefitContext) as NonCashBenefitState<NonCashDetailsFor<TNonCashBenefit>> | undefined;
  // Throw error that the Context is not injected, if it is undefined.
  if (ctx === undefined) {
    throw new Error('useNonCashState must be used within a NonCashStateProvider');
  }
  return ctx;
}
